import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { SubscriptionPagination } from '@adornis/pagination/paginatable.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import '@chancenlogistik/client/components/logistik-component-base-header';
import '@chancenlogistik/client/components/logistik-component-base-page-layout';
import { LogistikRoutes } from '@chancenlogistik/client/routes.js';
import { LogistikProduct } from '@chancenwerk/db/LogistikProduct.js';
import { LogistikProductFilter } from '@chancenwerk/db/LogistikProductFilter.js';
import {
  LogistikAdvertisingMaterialCategory,
  LogistikLearningMaterialCategory,
  LogistikProductCategory,
  LogistikProductTargetAudience,
  LogistikSchoolGrade,
  LogistikURLParameters,
} from '@chancenwerk/db/enums/LogistikEnums.js';
import '@chancenwerk/design/components/cr-dropdown-selection.js';
import '@chancenwerk/design/components/cr-grid.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-input.js';
import '@chancenwerk/design/components/cr-tabs.js';
import { html, nothing, type PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { Observable, debounceTime, distinctUntilChanged, of, switchMap, takeUntil, type Subscription } from 'rxjs';
import { LogistikProductOrderingAPI } from '../../../db/api/LogistikProductOrderingAPI.js';
import './logistik-component-product-card.js';

export const productOverviewSelectionSet: BaseQLSelectionSet<LogistikProduct> = {
  imageID: 1,
  category: 1,
  targetAudiences: 1,
  title: 1,
  availability: 1,
};

type ProductCategory = LogistikProductCategory | LogistikLearningMaterialCategory | LogistikAdvertisingMaterialCategory;

/**
 * @customElement logistik-component-product-overview
 */
@customElement('logistik-component-product-overview')
export class LogistikComponentProductOverview<T extends LogistikProduct> extends ChemistryLitElement {
  @property({ type: String }) heading = '';
  @property({ type: String }) backRoute = LogistikRoutes.landing;
  @property({ attribute: false }) selectionSet: BaseQLSelectionSet<T> = productOverviewSelectionSet;
  @property({ type: Number }) initialPagination = 20;
  @property({ type: Number }) paginationStep = 10;

  @property({ attribute: false }) orderingCategory = new RXController(
    this,
    undefined as LogistikProductCategory | undefined,
  );
  @state() protected readonly productOrdering = new RXController(this, this.orderingCategory.observable, obs =>
    obs.pipe(
      switchMap(category =>
        category ? LogistikProductOrderingAPI.subscribeByCategory(category as string)({ productIDs: 1 }) : of(null),
      ),
    ),
  );

  @property({ attribute: false }) categories = [] as Array<ProductCategory | string>;
  @property({ attribute: false }) categoriesSubsetForFilter = null;

  @property({ attribute: false }) subscription!: (
    skip: number,
    limit: number,
    ...args: LogistikProductFilter[]
  ) => (gqlFields: any) => Observable<T[]>;

  @property({ attribute: false }) productPagination?: SubscriptionPagination<T, LogistikProductFilter[]>;
  @property({ attribute: false }) filter = new RXController<LogistikProductFilter>(this, undefined);
  search?: (host: LogistikComponentProductOverview<T>) => Renderable;

  @state() protected readonly _products = new RXController<LogistikProduct[]>(this, []);

  protected _paginationSubscription?: Subscription;

  protected override willUpdate(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('productPagination')) {
      if (this._paginationSubscription) {
        this._paginationSubscription.unsubscribe();
        this._paginationSubscription = undefined;
      }
      if (this.productPagination) this._paginationSubscription = this.productPagination.data.subscribe(this._products);
    }
  }

  override connectedCallback(): void {
    super.connectedCallback();
    if (!this.filter.value) {
      const sps = routingState.searchParams.getValue();
      this.filter.next(
        new LogistikProductFilter({
          grade: parseStrEnum(LogistikSchoolGrade, sps.get(LogistikURLParameters.Grade)),
          search: sps.get(LogistikURLParameters.Search),
          category: parseProductCategory(
            this.categoriesSubsetForFilter ?? this.categories,
            sps.get(LogistikURLParameters.Category),
          ),
          targetAudiences: parseTargetAudiences(sps.get(LogistikURLParameters.TargetAudiences)),
        }),
      );
    }

    if (!this.productPagination)
      this.productPagination = new SubscriptionPagination(
        this.subscription,
        [new LogistikProductFilter({})],
        this.selectionSet,
        this.initialPagination,
        this.paginationStep,
      );

    if (this._paginationSubscription) {
      this._paginationSubscription.unsubscribe();
      this._paginationSubscription = undefined;
    }
    if (this.productPagination) this._paginationSubscription = this.productPagination.data.subscribe(this._products);

    this.filter.observable.pipe(takeUntil(this.disconnected)).subscribe(filter => {
      if (!this.productPagination) return;
      this.productPagination.updateParameters(filter);
    });

    routingState.searchParams
      .get(LogistikURLParameters.Category)
      .pipe(distinctUntilChanged(), takeUntil(this.disconnected))
      .subscribe(category => {
        if (!this.filter.value) return;
        this.filter.value.category = parseProductCategory(this.categoriesSubsetForFilter ?? this.categories, category);
        this.filter.next(this.filter.value);
      });

    routingState.searchParams
      .get(LogistikURLParameters.Search)
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.disconnected))
      .subscribe(searchString => {
        if (!this.filter.value) return;
        this.filter.value.search = searchString;
        this.filter.next(this.filter.value);
      });

    routingState.searchParams
      .get(LogistikURLParameters.Grade)
      .pipe(distinctUntilChanged(), takeUntil(this.disconnected))
      .subscribe(grade => {
        if (!this.filter.value) return;
        this.filter.value.grade = parseStrEnum(LogistikSchoolGrade, grade);
        this.filter.next(this.filter.value);
      });

    routingState.searchParams
      .get(LogistikURLParameters.TargetAudiences)
      .pipe(distinctUntilChanged(), takeUntil(this.disconnected))
      .subscribe(targetAudiences => {
        if (!this.filter.value) return;
        if (!targetAudiences) this.filter.value.targetAudiences = null;
        else {
          const parsed = parseTargetAudiences(targetAudiences);
          if (parsed.length) this.filter.value.targetAudiences = parsed;
          else this.filter.value.targetAudiences = null;
        }
        this.filter.next(this.filter.value);
      });

    if (!routingState.searchParams.getValue().get(LogistikURLParameters.Category) && this.categories[0])
      routingState.searchParams.set(LogistikURLParameters.Category, this.categories[0]);
  }

  override render() {
    return html`
      <logistik-component-base-page-layout>
        <logistik-component-base-header
          pageTitle=${this.heading}
          backRoute=${this.backRoute}
        ></logistik-component-base-header>
        <cr-flex space="lg">
          <cr-flex crossaxis-end> <slot name="top"></slot> </cr-flex> ${this._search()}
          ${this.categories.length
            ? html`
                <cr-tabs
                  .selected=${this.filter.value?.category}
                  ${css({ borderBottom: `1px solid ${this.colors.neutral.shade(300)}` })}
                >
                  ${repeat(
                    this.categories,
                    category => html`
                      <cr-tab
                        label=${category}
                        @click=${() => {
                          routingState.searchParams.set(LogistikURLParameters.Category, category);
                        }}
                      >
                        ${category}
                      </cr-tab>
                    `,
                  )}
                </cr-tabs>
              `
            : nothing}
          ${this._showProducts()}
        </cr-flex>
      </logistik-component-base-page-layout>
    `;
  }

  protected _showProducts() {
    if (this._products.value && !this._products.value.length)
      return html`
        <cr-empty-placeholder .img=${'close'} .text=${'Es gibt noch keine Produkte in dieser Kategorie'}>
        </cr-empty-placeholder>
      `;
    return html`
      ${this._products.value
        ? html`
            <cr-grid auto-fill-columns="250px" space="lg">
              ${repeat(
                this.productOrdering.value ? this._getOrderedProducts() : this._products.value,
                product => product._id,
                product =>
                  html` <logistik-component-product-card .product=${product}></logistik-component-product-card> `,
              )}
            </cr-grid>
          `
        : nothing}
    `;
  }

  protected _getOrderedProducts(): LogistikProduct[] {
    if (!this._products.value || !this.productOrdering.value) return [];
    const unorderedProducts = this._products.value.filter(p => !this.productOrdering.value!.productIDs.includes(p._id));
    const orderedProducts = this.productOrdering.value.productIDs
      .map(pid => this._products.value.find(p => p._id === pid))
      .filter(p => p !== undefined);
    return orderedProducts.concat(unorderedProducts);
  }

  protected _search() {
    return html`
      <x-flex horizontal space="md">
        <cr-input
          flex
          clearable
          placeholder="Nach Produkten suchen..."
          .value=${this.filter.value?.search}
          @value-changed=${(e: ValueEvent<string>) => {
            if (e.detail.value) routingState.searchParams.set(LogistikURLParameters.Search, e.detail.value);
            else routingState.searchParams.delete(LogistikURLParameters.Search);
          }}
        ></cr-input>
        <cr-dropdown-selection
          clearable
          placeholder="Jahrgangsstufen"
          .selectables=${Object.values(LogistikSchoolGrade)}
          .value=${this.filter.value?.grade}
          @value-picked=${(e: ValueEvent<LogistikSchoolGrade>) => {
            if (e.detail.value) routingState.searchParams.set(LogistikURLParameters.Grade, e.detail.value);
            else routingState.searchParams.delete(LogistikURLParameters.Grade);
          }}
        >
        </cr-dropdown-selection>
        ${this.search?.(this) ?? nothing}
      </x-flex>
    `;
  }
}

function parseTargetAudiences(value: string | null): LogistikProductTargetAudience[] {
  if (!value) return [];
  return value
    .split(',')
    .map(elm => parseStrEnum(LogistikProductTargetAudience, elm))
    .filter(elm => !!elm)
    .map(elm => elm as LogistikProductTargetAudience);
}
function parseStrEnum<E extends { [s: string]: string }>(enumType: E, value: string | null): E[keyof E] | null {
  if (value && (Object.values(enumType) as string[]).includes(value)) return value as E[keyof E];
  return null;
}
function parseProductCategory(allowedValues: string[], selectedValue: string | null): ProductCategory | null {
  if (selectedValue && allowedValues.includes(selectedValue)) return selectedValue as ProductCategory;
  return null;
}
